"use client";

import { cn, Modal, ModalContent } from "@heroui/react";
import { useDisclosure } from "@heroui/use-disclosure";
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState, useTransition } from "react";
import OTPInput from "react-otp-input";
import { Button } from "@/components/custom";
import { useTranslations } from "next-intl";
import { RequestOTP, SubmitOTP } from "@/lib/actions";
import Countdown from "react-countdown";
import { BankDetails } from "@/lib/declarations";
import CustomerServiceModal from "../modals/customer-service";
import FailModal from "../modals/fail-modal";
import { useModal } from "./modal";
import $ from "jquery";
import duration from "dayjs/plugin/duration";
import dayjs from "dayjs";
dayjs.extend(duration);

const INPUT_DIGIT = Number(process.env.NEXT_PUBLIC_INPUT_DIGIT);

interface OTPContextType {
  openOTPModal: (payload: BankDetails) => Promise<any>;
  openOTPSavedModal: () => Promise<any>;
}

const OTPContext = createContext<OTPContextType>({} as OTPContextType);

export const OTPProvider = ({ children }: { readonly children: ReactNode }) => {
  const t = useTranslations();

  const countdownRef = useRef<Countdown>(undefined);
  const resolveRef = useRef<(value?: any | PromiseLike<void>) => void>(undefined);
  const rejectRef = useRef<(reason?: any) => void>(undefined);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const noticeDisclosure = useDisclosure();

  const [otp, setOtp] = useState<string>();
  const [token, setToken] = useState<string>();
  const [resendVisibility, SetResendVisibility] = useState<boolean>(true);
  const [date, setDate] = useState(Date.now() + 60 * 1000);
  const [invalid, setInvalid] = useState(false);
  const [otpExpired, setOtpExpired] = useState(false);
  const [pending, startTransition] = useTransition();
  const [payload, setPayload] = useState<BankDetails>();
  const { openModal: OpenExternalModal } = useModal();

  const onResend = () => startTransition(async () => await PerformRequestOTP());
  const PerformRequestOTP = async () => {
    ResetStates();
    const res = await RequestOTP();
    console.log(res);

    switch (res.status_code) {
      case 0:
        if (res.data?.expired_at) {
          setDate(res.data?.expired_at * 1000);
          console.log(res.data?.expired_at);
        }
        setToken(res.token);
        localStorage.setItem("otp_id", String(res.token));
        if (typeof process.env.NEXT_PUBLIC_MANUAL_OTP != "undefined" && process.env.NEXT_PUBLIC_MANUAL_OTP == "true") {
          OnCloseModal.cancel();
          noticeDisclosure.onOpen();
          return Promise.reject("otp_request_cc");
        }
        break;
      case 2:
      case 8:
        if (typeof res.data !== "undefined" && "expired_at" in res.data) {
          OpenExternalModal(<FailModal description={t("exceeded-request-countdown")} />, t("operation-failed"));
          return Promise.reject("otp_request_failed");
        }
        OnCloseModal.cancel();
        noticeDisclosure.onOpen();
        return Promise.reject("otp_request_cc");
      default: {
        OpenExternalModal(<FailModal description={t("exceeded-request")} />, t("failed-to-add-bank-account"));
        return Promise.reject("otp_request_failed");
      }
    }

    countdownRef.current?.start();
    return Promise.resolve();
  };

  const openModal: (payload: BankDetails) => Promise<unknown> = async (payload) => {
    if (typeof process.env.NEXT_PUBLIC_OTP_ENABLE == "undefined" || process.env.NEXT_PUBLIC_OTP_ENABLE == "false") {
      noticeDisclosure.onOpen();
      return;
    }

    SetResendVisibility(true);
    setPayload(payload);

    resolveRef.current = undefined;
    rejectRef.current = undefined;
    localStorage.removeItem("otp_id");

    return PerformRequestOTP()
      .then(() => {
        onOpen();
        return new Promise((resolve, reject) => {
          resolveRef.current = resolve;
          rejectRef.current = reject;
        });
      })
      .catch((error) => {
        // closeModal(true, error);
        OnCloseModal.reject(error);
        return Promise.reject(error);
      });
  };

  const openSavedModal = () => {
    OnCloseModal.cancel();
    SetResendVisibility(false);
    setOtp("");
    onOpen();
    return new Promise((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
    });
  };

  const OnCloseModal = {
    cancel: function () {
      setInvalid(false);
      ResetStates();
      onClose();
    },
    resolve: function (res: any) {
      if (resolveRef.current) {
        /** Event listener declare in /instrumentation-client.ts */
        $(document).trigger("notify-form-reset");
        $(document).trigger("notify-modal-close");
        resolveRef.current(res);
        ResetStates();
        onClose();
      }
    },
    reject: function (reason: any) {
      if (rejectRef.current) {
        rejectRef.current(reason);
        ResetStates();
        onClose();
      }
    },
  };

  const ResetStates = () => {
    // setPayload(undefined);
    setOtp(undefined);
    setOtpExpired(false);
    setInvalid(false);
    setDate(Date.now() + 60 * 1000);

    // localStorage.removeItem("otp_id");
  };

  const onSubmit = useCallback(
    () =>
      startTransition(async () => {
        console.log(payload);

        if (!payload) return;
        const res = await SubmitOTP(token!, Number(otp), payload);
        console.log(res);
        if (res.status_code === 0) {
          console.log("OTP Verified");
          OnCloseModal.resolve(res);
          return;
        } else if (res.status_code === 8) {
          noticeDisclosure.onOpen();
        }
        setInvalid(true);
      }),
    [otp, payload]
  );

  return (
    <OTPContext.Provider value={{ openOTPModal: openModal, openOTPSavedModal: openSavedModal }}>
      {children}
      <CustomerServiceModal type="BANK" disclosure={noticeDisclosure} />
      <Modal
        isOpen={isOpen}
        onClose={() => OnCloseModal.cancel()}
        size="sm"
        scrollBehavior="inside"
        classNames={{
          body: "bg-[#021517]",
          header: "bg-[#0A2A27] shadow-lg rounded-t-lg",
          closeButton: cn("hover:bg-white/5 active:bg-white/10"),
        }}
        className="z-[101]"
        placement="center"
        radius="lg"
        isDismissable={false}
      >
        <ModalContent className="p-12">
          <div className="flex w-full flex-col items-center gap-4">
            <div className="text-center text-[24px] font-bold">{t("otp-text-header")}</div>
            <div
              className="mt-4 flex flex-col gap-2"
              onFocus={() => {
                setInvalid(false);
              }}
            >
              <OTPInput
                value={otp}
                onChange={setOtp}
                numInputs={INPUT_DIGIT}
                inputType="number"
                // renderSeparator={<div className="w-2" />}
                renderInput={(props) => (
                  <input
                    {...props}
                    className="mx-1 aspect-square !h-auto !w-12 rounded-lg border text-center text-lg focus:!outline-yellow-400"
                  />
                )}
              />
              {invalid && <span className="text-center text-tiny text-danger">{t("otp-error-text")}</span>}
            </div>
            {resendVisibility && (
              <div
                className={cn("my-4 text-sm", {
                  "text-gray-400": pending,
                })}
              >
                {otpExpired ? (
                  <>
                    {t.rich("otp-text-b", {
                      resend: (children) => (
                        <span role="button" className="text-primary" onClick={onResend}>
                          {children}
                        </span>
                      ),
                    })}
                  </>
                ) : (
                  <>
                    {t.rich("otp-text-a", {
                      countdown: () => (
                        <Countdown
                          date={date}
                          onComplete={() => setOtpExpired(true)}
                          renderer={({ minutes, seconds }) =>
                            `${minutes.toLocaleString(undefined, {
                              minimumIntegerDigits: 2,
                            })}:${seconds.toLocaleString(undefined, {
                              minimumIntegerDigits: 2,
                            })}`
                          }
                        />
                      ),
                    })}
                  </>
                )}
              </div>
            )}
            <div className="my-4 w-full">
              <Button
                onPress={onSubmit}
                className="w-full font-bold text-black disabled:bg-slate-400"
                isDisabled={otp?.length !== INPUT_DIGIT || pending}
              >
                {t("submit")}
              </Button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </OTPContext.Provider>
  );
};

export const useOTP = () => {
  const context = useContext(OTPContext);
  if (context === undefined) {
    throw new Error("useSecurityPin must be used within a SecurityPinProvider");
  }
  return context;
};
